/**
 * `ACAD_TABLE` × an ORIENTATION-REVERSING affine — the ONE truth for "this
 * edit declines this table", shared by
 * {@link import("./geometryTransforms").transformEntityCoords} (which leaves
 * the declined entity byte-identical, so the DOCUMENT reducer and the
 * dynamic-block evaluator both decline through the same call) and the SHELL
 * reducer (which reports the refusal as an RU+EN warning-tone audit row).
 *
 * Same two-site shape as
 * {@link import("./tableScaleRefusal").isTableScaleRefused} and
 * {@link import("./oleFrameTransformRefusal").isOleFrameTransformRefused} —
 * the validity predicate lives here in `command-engine` so the transform arm's
 * guard and the audit message can never drift apart.
 *
 * ## Why a table declines a MIRROR — @codex PR #785 round 15, FINDING 2
 *
 * A `Table` is placed exactly like the block reference it is in DXF: an
 * insertion point (10/20/30) and a horizontal-direction VECTOR (11/21/31).
 * The `transformEntityCoords` `Table` arm maps both, which is EXACT for every
 * proper rigid motion — a MOVE translates the insertion point, a ROTATE turns
 * the direction vector with it, and the pre-evaluated `*T<N>` graphics block
 * follows because `tableAsBlockReference` reads that same vector.
 *
 * A REFLECTION is not a proper rigid motion, and the pair cannot express one.
 * Take a table whose local graphics extend RIGHT and DOWN from the insertion
 * point — the AutoCAD default flow — and mirror it about the Y axis. The
 * linear part is `diag(-1, 1)`, so `horizontalDirection` `(1, 0)` maps to
 * `(-1, 0)`. But `(-1, 0)` is not "reflected": it is the direction a table
 * TURNED BY 180° has, and `tableAsBlockReference` hard-codes
 * `scaleX/scaleY/scaleZ = 1`, so the `*T<N>` content is re-placed as a 180°
 * ROTATION — extending LEFT and UP. The required image extends left and DOWN,
 * and `AcDbTable` has no scale, no mirror flag and no handedness field that
 * can name it. Writing the turn is therefore the same class of silent
 * corruption `oleFrameTransformRefusal.ts` was written to stop: the picture on
 * screen is not the picture the edit asked for, and `dxf-io/writer.ts` makes
 * it the file's truth on the next save.
 *
 * ## The precedent this REUSES rather than re-decides
 *
 * `./explodeBlockChildPolylineTableOle.ts::explodeChildRefusal` already
 * reached this verdict from the other direction: a `Table` child is carryable
 * only when `|sx| = |sy| = 1 AND sx·sy > 0`, and its own comment names the
 * third conjunct "the ORIENTATION test: the first two admit every
 * unit-magnitude placement, including the two mirrors, and only `sx*sy > 0`
 * separates a proper rotation from a reflection". `sx·sy` IS the determinant
 * of that placement's diagonal linear part, so `"table-not-rigid"` and this
 * module's predicate are the same rule stated for the two different inputs
 * each site has: an AUTHORED INSERT scale pair there, a PROBED affine here.
 * (The same split `oleFrameTransformRefusal.ts`' header records between its
 * own probed predicate and that module's authored-angle one.)
 *
 * ## Which maps are refused, and which are NOT
 *
 *   | edit                                     | det(L) | verdict |
 *   |------------------------------------------|--------|---------|
 *   | MOVE (translation)                       |  +1    | applied |
 *   | ROTATE by any angle                      |  +1    | applied |
 *   | point reflection (ROTATE 180°)           |  +1    | applied |
 *   | SCALE by `factor` (`diag(f, f)`)         |  +f²   | applied |
 *   | MIRROR about ANY axis                    |  < 0   | REFUSED |
 *   | a dynamic-block FLIP action              |  < 0   | REFUSED |
 *
 * SCALE is deliberately not this module's business: a `geometry/scale-entities`
 * factor is ONE scalar applied to both axes, so even a negative factor gives
 * `det = f² > 0` (the point reflection, which a table expresses exactly
 * through a 180° `horizontalDirection`). A genuine RESIZE is declined by
 * {@link import("./tableScaleRefusal").isTableScaleRefused} for a DIFFERENT
 * reason — the `*T<N>` block has no generator — and the two predicates are
 * independent: a `|factor| !== 1` scale is refused there and passes here, a
 * mirror is refused here and never reaches there.
 *
 * ## What "refused" means
 *
 * The SAME `entity` object is returned, so a refusal is a true no-op that no
 * downstream identity check can mistake for an edit — the convention
 * `transformEntityCoords`' `OLE Frame` arm already relies on, and the one
 * `block-evaluator/geometryPolylineTableOle.ts` reads as "the delegate
 * DECLINED" when it decides whether to touch the entity's size fields.
 *
 * COMPLETING THIS requires, in its own PR, the same thing
 * `tableScaleRefusal.ts` names: a table layout engine that can REGENERATE the
 * `*T<N>` graphics — here reflected rather than rescaled — wired so the
 * content rebuild and the placement write land in ONE `EntityMutation`. Only
 * then can this predicate be deleted. Until that exists the refusal is
 * VISIBLE rather than silent, per CLAUDE.md ПРИОРИТЕТ #1's one allowed
 * exception: a documented, known-incomplete subsystem.
 *
 * RESIDUAL 1 — CLOSED (@codex PR #785 round 16, FINDING 2). MIRROR-KEEP-SOURCE
 * USED TO CLONE THE DECLINED ENTITY IN PLACE. `geometry/mirror-entities` with
 * `deleteSource: false` goes through
 * `apps/web/.../state/geometryReducerEdit.ts`' `cloneSelectionWithMutations`,
 * whose `transform` callback had no way to say "make no clone": it returned an
 * entity, and for a declined one that entity is the SOURCE, so the batch gained
 * a byte-identical copy stacked on the original at a fresh id — while the audit
 * row said the table had been DECLINED. Returning the unchanged entity is a
 * true no-op for an IN-PLACE edit and was never one for an APPENDING edit, so
 * the round-15 note calling this a residual of the family understated it: it
 * was a user-visible defect, and two contradictory statements about the same
 * edit.
 *
 * It was a property of the refusal FAMILY rather than of this predicate, and
 * the repair is too: `transform` may now answer `null`, the helper then mints
 * no id and appends nothing, and the mirror arm asks BOTH fn-shaped refusal
 * predicates — {@link isTableMirrorRefused} and
 * {@link import("./oleFrameTransformRefusal").isOleFrameTransformRefused} —
 * the same two the shell reducer summarises for its warning rows, so the
 * document and the message cannot disagree. The third family member,
 * {@link import("./tableScaleRefusal").isTableScaleRefused}, takes a scalar
 * factor and belongs to `geometry/scale-entities`, which has no keep-source
 * clone path at all and already answered `null` from its own in-place arm.
 * Pinned at dispatch level for BOTH reachable members, in
 * `apps/web/…/editorStore.tableMirrorRefusal.test.ts` and
 * `…/editorStore.oleFrameTransformRefusal.test.ts`.
 *
 * RESIDUAL 2 — CLOSED. Both halves are now complete for both paths. The
 * DOCUMENT half always was: the direct commands and the dynamic-block
 * evaluator both reach the table through `transformEntityCoords`, so neither
 * can write the turn. The dynamic-block AUDIT half briefly was not — a FLIP of
 * a table was declined correctly and reported NOWHERE, because the evaluator
 * passed the byte-identical entity through with an empty
 * `EvaluateBlockResult.errors`. It is now announced at the evaluator's own
 * sink: `packages/block-evaluator/src/geometryPolylineTableOle.ts`'s
 * `transformPolylineTableOleChild` consults {@link isTableMirrorRefused} — this
 * module's predicate, imported rather than restated — and emits the
 * `"table-mirror"` row `actionRefusalMessages.ts` carries in RU + EN beside
 * `"table-resize"` and `"ole-frame-oblique"`. That one shared funnel covers
 * BOTH dispatchers that can reach `det < 0`: the Flip action and a single-axis
 * Scale, whose `(-1, 1)` clears `isTableScaleRefused` (both magnitudes are 1)
 * yet is still a reflection.
 */

import type { DrawingEntity, DrawingSpace } from "@dxf-ai-editor/drawing-model";

import { probeAffine2d, type TransformFn } from "./affine2dProbe";
import {
  summarizeSelectionRefusal,
  type SelectionRefusalSummary,
} from "./selectionRefusalWalk";

/**
 * `true` when `fn` REVERSES orientation (handedness): `det(L) < 0`.
 *
 * MIRROR is the only DIRECT edit in this codebase that produces `det < 0` — a
 * `geometry/scale-entities` factor is a SINGLE scalar applied to both axes
 * (`scalePoint`), so even a negative factor gives `det = f² > 0` (a point
 * reflection = 180° rotation, which PRESERVES handedness and must NOT flip a
 * bulge). Translation and rotation are `det = +1`. A dynamic-block FLIP action
 * composes a per-axis `-1` and does reach it.
 *
 * TWO consumers, which is why it lives HERE rather than inside
 * `geometryTransforms.ts` where it was written (it was private there, and this
 * module is the second reader — `.claude/skills/dep-graph-refactor`):
 *   - the heavy-POLYLINE arm's `flipBulges`: a signed bulge encodes WHICH SIDE
 *     of the chord its arc bows to, so a reflection has to negate it;
 *   - {@link isTableMirrorRefused}: a reflection is the one placement an
 *     `ACAD_TABLE`'s insertion point + horizontal direction cannot name.
 *
 * The affine recovery is {@link probeAffine2d} — the ONE three-probe affine
 * reader this package has, shared with `ellipseEditTransform.ts`,
 * `oleFrameTransformRefusal.ts` and `geometryTransforms.ts`' `linearPart`.
 */
export function isOrientationReversingMap(fn: TransformFn): boolean {
  const { a, b, c, d } = probeAffine2d(fn);
  return a * d - b * c < 0;
}

/**
 * `true` when an in-place edit whose affine is `fn` must DECLINE `entity`
 * rather than commit a 180° TURN where a reflection was asked for. See the
 * module header.
 *
 * Total and pure: every non-`Table` entity transforms normally, and so does a
 * table under any orientation-PRESERVING map (every MOVE, every ROTATE, every
 * SCALE this app can dispatch).
 */
export function isTableMirrorRefused(
  entity: DrawingEntity,
  fn: TransformFn,
): boolean {
  return entity.type === "Table" && isOrientationReversingMap(fn);
}

/**
 * What an in-place transform whose affine is `fn` will actually do to
 * `entityIds`: which of them are tables this map cannot express (declined, in
 * document order) and how many entities WILL be transformed.
 *
 * The walk itself — id resolution, de-duplication, "an id that matches nothing
 * counts as neither", document order — is
 * {@link import("./selectionRefusalWalk").summarizeSelectionRefusal}, the ONE
 * such walk this package has, shared with the SCALE × `Table` refusal
 * (`tableScaleRefusal.ts`) and the OLE2FRAME transform refusal
 * (`oleFrameTransformRefusal.ts`). Only {@link isTableMirrorRefused} and the
 * message ids the shell reducer picks are this refusal's own.
 *
 * `fn` is re-probed per candidate entity rather than once up front, for the
 * reason `summarizeOleFrameTransformRefusal`'s doc gives at length: hoisting
 * the map test and re-testing `entity.type === "Table"` inline here would
 * restate the predicate at a second site, which is the drift this two-site
 * shape exists to prevent.
 */
export function summarizeTableMirrorRefusal(
  spaces: readonly DrawingSpace[],
  entityIds: readonly string[],
  fn: TransformFn,
): SelectionRefusalSummary {
  return summarizeSelectionRefusal(spaces, entityIds, (entity) =>
    isTableMirrorRefused(entity, fn),
  );
}
